import React from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import { Sun, CloudSun, Zap, ZapOff, Wind, Refrigerator, Droplets, Snowflake } from 'lucide-react';

export const EnvironmentalControls: React.FC = () => {
  const {
    sunlightIntensity,
    setSunlight,
    gridAvailable,
    toggleGrid,
    selectedAppliances,
    updateApplianceCount,
    activeLoadWatts,
  } = useSolarStore();

  // Helper to check appliance count for specific Nigerian standard appliances
  const getCount = (id: string) => {
    const item = selectedAppliances.find(a => a.id === id);
    return item ? item.count : 0;
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl text-white space-y-8 my-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-800">
        <div>
          <h3 className="text-xl font-bold flex items-center gap-2">
            <CloudSun className="w-6 h-6 text-emerald-400" />
            Environmental & Grid Simulation Controls
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Simulate African weather (harmattan dust, cloud cover) and PHCN grid blackouts in real-time.
          </p>
        </div>
        <div className="text-right">
          <span className="text-xs text-slate-400 block">Total Active Load</span>
          <span className="text-lg font-black text-emerald-400">{activeLoadWatts} Watts</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        
        {/* 1. Sunlight & Dust Intensity Slider (0% - 100%) */}
        <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-4">
          <div className="flex justify-between items-center">
            <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
              <Sun className="w-4 h-4 text-amber-400" /> Sunlight & Dust Intensity
            </label>
            <span className="text-amber-400 font-extrabold text-sm">{sunlightIntensity}%</span>
          </div>
          <input
            type="range"
            min="10"
            max="100"
            step="5"
            value={sunlightIntensity}
            onChange={(e) => setSunlight(Number(e.target.value))}
            className="w-full accent-amber-500 cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-slate-500">
            <span>Harmattan / Heavy Clouds (10%)</span>
            <span>Clear Sky (100%)</span>
          </div>
        </div>

        {/* 2. PHCN Grid Status Toggle */}
        <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 flex flex-col justify-between">
          <div className="flex justify-between items-center">
            <span className="text-xs font-bold text-slate-300 flex items-center gap-2">
              {gridAvailable ? <Zap className="w-4 h-4 text-emerald-400" /> : <ZapOff className="w-4 h-4 text-rose-400" />}
              PHCN Grid Status
            </span>
            <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${
              gridAvailable ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
            }`}>
              {gridAvailable ? 'Grid Available' : 'Blackout Mode'}
            </span>
          </div>
          <div className="flex items-center justify-between pt-4">
            <span className="text-xs text-slate-400">
              {gridAvailable ? 'Grid stable, supplementing solar.' : 'Total reliance on solar & inverter battery.'}
            </span>
            <button
              onClick={toggleGrid}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                gridAvailable ? 'bg-emerald-600' : 'bg-slate-800'
              }`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                gridAvailable ? 'translate-x-8' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>

        {/* 3. Quick Nigerian Appliance Toggles */}
        <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-3 lg:col-span-1 md:col-span-2">
          <div className="text-xs font-bold text-slate-300 flex items-center gap-2">
            <Wind className="w-4 h-4 text-teal-400" /> Quick Appliance Toggles
          </div>
          <div className="grid grid-cols-2 gap-2">
            {/* Standing Fan (60W) */}
            <button
              onClick={() => updateApplianceCount('lighting', getCount('lighting') > 0 ? 0 : 1)}
              className={`p-2.5 rounded-xl text-left border transition-all text-xs flex items-center justify-between ${
                getCount('lighting') > 0 ? 'bg-emerald-950/40 border-emerald-500 text-white font-bold' : 'bg-slate-900 border-slate-800 text-slate-400'
              }`}
            >
              <span>Fan (60W)</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
                {getCount('lighting') > 0 ? 'ON' : 'OFF'}
              </span>
            </button>

            {/* Refrigerator (250W) */}
            <button
              onClick={() => updateApplianceCount('fridge', getCount('fridge') > 0 ? 0 : 1)}
              className={`p-2.5 rounded-xl text-left border transition-all text-xs flex items-center justify-between ${
                getCount('fridge') > 0 ? 'bg-emerald-950/40 border-emerald-500 text-white font-bold' : 'bg-slate-900 border-slate-800 text-slate-400'
              }`}
            >
              <span>Fridge (250W)</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
                {getCount('fridge') > 0 ? 'ON' : 'OFF'}
              </span>
            </button>

            {/* Water Borehole Pump (750W) */}
            <button
              onClick={() => updateApplianceCount('pumping_machine', getCount('pumping_machine') > 0 ? 0 : 1)}
              className={`p-2.5 rounded-xl text-left border transition-all text-xs flex items-center justify-between ${
                getCount('pumping_machine') > 0 ? 'bg-emerald-950/40 border-emerald-500 text-white font-bold' : 'bg-slate-900 border-slate-800 text-slate-400'
              }`}
            >
              <span>Water Pump (750W)</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
                {getCount('pumping_machine') > 0 ? 'ON' : 'OFF'}
              </span>
            </button>

            {/* 1.5HP Inverter AC (1200W) */}
            <button
              onClick={() => updateApplianceCount('ac_1hp', getCount('ac_1hp') > 0 ? 0 : 1)}
              className={`p-2.5 rounded-xl text-left border transition-all text-xs flex items-center justify-between ${
                getCount('ac_1hp') > 0 ? 'bg-emerald-950/40 border-emerald-500 text-white font-bold' : 'bg-slate-900 border-slate-800 text-slate-400'
              }`}
            >
              <span>1.5HP AC (1200W)</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
                {getCount('ac_1hp') > 0 ? 'ON' : 'OFF'}
              </span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
